import { computed, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { PageDefinition } from '@app/shared/models/page-definition';
import { PagedListImpl } from '@app/utils/lists/paged-list/paged-list-impl';
import { PagedListOptions } from '@app/utils/lists/paged-list/paged-list-options';
import { watch } from '@app/utils/signals/watch';

export function pagedList<T>(allItems: Signal<T[]>, options?: Omit<PagedListOptions<T>, 'entries'>) {
  const pagedList = new PagedListImpl({
    entries: allItems() ?? [],
    page: options?.page ?? 1,
    pageSize: options?.pageSize ?? 10,
    sort: options?.sort,
  });

  const state = toSignal(pagedList.page$);
  const empty = computed(() => state().total === 0);
  const emptyPage = computed(() => state().hidden === state().total);
  const items = computed(() => state().entries);
  const page = computed(() => state().page);
  const pageSize = computed(() => state().pageSize);

  watch([allItems], ([allItems]) => {
    pagedList.updateEntries(allItems).draw();
  });

  return {
    allItems,
    empty,
    emptyPage,
    instance: pagedList,
    items,
    page,
    pageSize,
    state,

    // Methods
    goTo,
    sort,
    updateFilter,
    updatePageSize,
    updateFilteredEntries,
  };

  function goTo(page: PageDefinition, draw: boolean = true): void {
    pagedList.goTo(page);
    update(draw);
  }

  function sort(properties: string[], reverse?: boolean): void {
    pagedList.sortBy(properties, reverse);
  }

  function updateFilter(filter: string, draw: boolean = true): void {
    pagedList.filter(filter);
    update(draw);
  }

  function updateFilteredEntries(entries: T[], draw: boolean = true): void {
    pagedList.updateFilteredEntries(entries);
    update(draw);
  }

  function updatePageSize(pageSize: number, draw: boolean = true): void {
    pagedList.updatePageSize(pageSize);
    update(draw);
  }

  function update(draw: boolean): void {
    if (draw) {
      pagedList.draw();
    }
  }
}
